import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, LockKey, Lightning, GraduationCap, HandHeart, Bed, Buildings, Database, ChartBar, Certificate } from "@phosphor-icons/react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_encrypted-school-hub/artifacts/525b6ivc_WhatsApp%20Image%202026-05-12%20at%209.48.59%20PM.jpeg";
const TEXTURE = "https://images.unsplash.com/photo-1760442903566-99f3eae250ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx3YXJtJTIwbmV1dHJhbCUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Nzk3NjY0NXww&ixlib=rb-4.1.0&q=85";
const ARCH = "https://customer-assets.emergentagent.com/job_encrypted-school-hub/artifacts/525b6ivc_WhatsApp%20Image%202026-05-12%20at%209.48.59%20PM.jpeg";

function NavBar() {
  return (
    <nav className="sticky top-0 z-40 bg-[var(--vs-bg)]/85 backdrop-blur-md border-b border-[var(--vs-border)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">Vidya Seva</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Management System</div>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-sm">
          <a href="#features" className="hover:text-[var(--vs-secondary)] transition-colors">Modules</a>
          <a href="#security" className="hover:text-[var(--vs-secondary)] transition-colors">Security</a>
          <a href="#scale" className="hover:text-[var(--vs-secondary)] transition-colors">Scale</a>
          <a href="#roles" className="hover:text-[var(--vs-secondary)] transition-colors">Roles</a>
          <Link to="/verify" data-testid="nav-verify-link" className="hover:text-[var(--vs-secondary)] transition-colors">Verify a certificate</Link>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/login?role=trust"
            data-testid="nav-trust-login-button"
            className="hidden sm:inline-flex px-4 py-2 text-sm rounded-md border border-[var(--vs-secondary)] text-[var(--vs-secondary)] hover:bg-[var(--vs-secondary)] hover:text-[var(--vs-bg)] transition-all"
          >
            Trust login
          </Link>
          <Link
            to="/login"
            data-testid="nav-login-button"
            className="px-4 py-2 text-sm rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] transition-all"
          >
            Sign in
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="eyebrow mb-5">Trust • Transparency • Lifelong impact</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold text-[var(--vs-text)]">
            One platform for{" "}
            <span className="italic font-light text-[var(--vs-secondary)]">5,000 schools</span>,{" "}
            <span className="italic font-light text-[var(--vs-secondary)]">5,000 hostels</span>{" "}
            and every child whose future they hold.
          </h1>
          <p className="mt-6 max-w-2xl text-base lg:text-lg leading-relaxed text-[var(--vs-text-secondary)]">
            VSMS is a unified, AES-256 encrypted school + hostel SaaS designed for welfare trusts at national scale —
            up to <span className="font-mono text-[var(--vs-secondary)] font-medium">1 crore students</span> on a single
            tenant. Admissions, fees, progress, donor portal & certificates — encrypted, audited, role-isolated.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/login?role=trust"
              data-testid="hero-cta-trust-login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] font-medium hover:bg-[var(--vs-secondary-hover)] transition-all hover:-translate-y-px"
            >
              <Buildings size={16} weight="fill" /> Trust Admin login
            </Link>
            <Link
              to="/login"
              data-testid="hero-cta-login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[var(--vs-primary)] text-[var(--vs-text)] font-medium hover:bg-[var(--vs-primary-hover)] transition-all hover:-translate-y-px"
            >
              Sign in to dashboard
              <Lightning size={16} weight="fill" />
            </Link>
            <a
              href="#features"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-[var(--vs-text)] text-[var(--vs-text)] hover:bg-[var(--vs-text)] hover:text-[var(--vs-bg)] transition-all"
            >
              Tour the modules
            </a>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
              <LockKey size={14} weight="fill" /> AES-256-GCM
            </div>
            <div className="w-px h-4 bg-[var(--vs-border)]" />
            <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
              <ShieldCheck size={14} weight="fill" /> TLS 1.2+
            </div>
            <div className="w-px h-4 bg-[var(--vs-border)]" />
            <div className="flex items-center gap-1.5 text-xs font-mono text-[var(--vs-secondary)]">
              <Database size={14} weight="fill" /> Per-role isolation
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-[var(--vs-border)]">
            <img src={HERO_IMG} alt="Vidya Seva students at the hostel" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-80">VS-2026-0001</div>
              <div className="font-display text-xl font-semibold mt-1">Riya Sharma — Class 8</div>
              <div className="text-xs opacity-90">Sponsored • Hostel A • Progress 86/100</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* KPI bar */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--vs-border)] border border-[var(--vs-border)] rounded-md overflow-hidden">
        {[
          { v: "5,000", l: "Schools" },
          { v: "5,000", l: "Hostels" },
          { v: "1 Cr", l: "Student capacity" },
          { v: "AES-256", l: "PII at rest" },
        ].map((k) => (
          <div key={k.l} className="bg-[var(--vs-bg)] px-6 py-5">
            <div className="font-display text-3xl tracking-tight font-semibold">{k.v}</div>
            <div className="eyebrow mt-1">{k.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: GraduationCap, title: "Admissions with mandatory medical gate", body: "Status pinned to Draft until medical certificate uploads — enforced at the API and DB layer. No workaround.", span: "lg:col-span-7" },
    { icon: Bed, title: "Real-time hostel occupancy", body: "Color-coded alerts: Green <75%, Amber <90%, Red ≥90%. Per-hostel scope, super-admin gets the heatmap.", span: "lg:col-span-5" },
    { icon: HandHeart, title: "Donor Portal", body: "Sponsored students only. Monthly photo gallery, 4-dimension progress, marksheets, 80G receipts — donor isolation enforced via JOIN.", span: "lg:col-span-5" },
    { icon: ChartBar, title: "Marksheet workflow", body: "Teacher enters subject marks → Admin reviews → Published to donor. Teachers cannot see other subjects.", span: "lg:col-span-7" },
    { icon: Certificate, title: "Auto-numbered certificates", body: "VS/TC/2026/0001, VS/CC/2026/0001 — generated as PDF, stored privately in object storage with signed access.", span: "lg:col-span-4" },
    { icon: Buildings, title: "Bulk promotion / demotion", body: "End-of-year orchestration. Audit trail captures every transition.", span: "lg:col-span-4" },
    { icon: Database, title: "Audit logs", body: "Every login, admission, fee, transfer, certificate — captured immutably with actor, role, IP.", span: "lg:col-span-4" },
  ];
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">Modules built for welfare trusts</div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-semibold">Everything a trust runs, in one panel.</h2>
        </div>
        <Link to="/login" className="hidden md:inline-flex text-sm underline underline-offset-4 hover:text-[var(--vs-secondary)]">Open dashboard →</Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className={`p-7 rounded-md border border-[var(--vs-border)] bg-white hover:bg-[var(--vs-bg-subtle)] transition-colors ${it.span}`}
            >
              <Icon size={26} weight="duotone" className="text-[var(--vs-secondary)] mb-4" />
              <div className="font-display text-xl font-semibold leading-snug">{it.title}</div>
              <p className="mt-2 text-sm text-[var(--vs-text-secondary)] leading-relaxed">{it.body}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function Security() {
  return (
    <section id="security" className="bg-[var(--vs-bg-inverse)] text-[var(--vs-text-inverse)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[var(--vs-primary)] mb-3">Security & Privacy</div>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
            Encryption isn't a feature.{" "}
            <span className="italic font-light text-[var(--vs-primary)]">It's the foundation.</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-white/75">
            Every PII column — Aadhar, address, phone, guardian name — is sealed with{" "}
            <span className="font-mono text-[var(--vs-primary)]">AES-256-GCM</span>{" "}
            before it ever touches MongoDB. In transit, every byte rides{" "}
            <span className="font-mono text-[var(--vs-primary)]">TLS 1.2+</span>. Roles are enforced at the dependency layer of every endpoint.
          </p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { t: "AES-256-GCM at rest", b: "Sensitive fields wrapped with 96-bit nonce + 128-bit auth tag. Stored as base64(nonce||ct||tag)." },
            { t: "TLS 1.2+ in transit", b: "Platform-managed certificates, automatic renewal." },
            { t: "Bcrypt password hashing", b: "Cost factor 12, per-user salt. Plaintext never persisted." },
            { t: "Per-role isolation", b: "Hostel/School admins scoped by ID; teachers locked to their subject; donors locked to assignment." },
            { t: "JWT cookies", b: "HttpOnly + Secure cookies, 12-hour TTL, signed HS256." },
            { t: "Audit log every action", b: "Login, admission, fee, transfer, certificate — actor, role, IP, timestamp." },
          ].map((it, i) => (
            <div key={it.t} className="p-5 rounded-md border border-white/10 bg-white/5">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--vs-primary)]">0{i + 1}</div>
              <div className="font-display text-lg font-semibold mt-2">{it.t}</div>
              <p className="text-xs text-white/65 mt-1.5 leading-relaxed">{it.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Scale() {
  return (
    <section id="scale" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/3] rounded-md overflow-hidden border border-[var(--vs-border)]">
            <img src={ARCH} alt="Vidya Seva hostel" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-6 hidden md:block aspect-square w-44 rounded-md overflow-hidden border border-[var(--vs-border)]">
            <img src={TEXTURE} alt="Texture" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="eyebrow mb-3">Built for national scale</div>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
            From a 100-student trust to a{" "}
            <span className="italic font-light text-[var(--vs-secondary)]">10-million-student</span> network.
          </h2>
          <p className="mt-5 text-sm lg:text-base leading-relaxed text-[var(--vs-text-secondary)]">
            VSMS is engineered for horizontal scale: stateless FastAPI workers, MongoDB sharding on tenant + student
            keys, async I/O end-to-end, and Emergent object storage for files. Read replicas, regional caches, and CDN
            asset delivery keep the donor portal snappy whether a trust runs one hostel or five thousand.
          </p>
          <div className="mt-7 grid grid-cols-3 gap-px bg-[var(--vs-border)] border border-[var(--vs-border)] rounded-md overflow-hidden">
            {[{ v: "≤120ms", l: "API p95" }, { v: "1 Cr", l: "students" }, { v: "99.95%", l: "uptime SLO" }].map((k) => (
              <div key={k.l} className="bg-[var(--vs-bg)] p-5">
                <div className="font-display text-2xl font-semibold">{k.v}</div>
                <div className="eyebrow mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Roles() {
  const roles = [
    { t: "Super Admin", b: "Owns hostels, schools, users, audit logs." },
    { t: "Hostel Admin", b: "Single hostel scope: admissions, fees, progress, photos." },
    { t: "School Admin", b: "Single school scope: marksheets, fees, promotions." },
    { t: "Teacher", b: "Subject-scoped — marks for own subject only." },
    { t: "Donor", b: "Assignment-scoped — sponsored students only." },
  ];
  return (
    <section id="roles" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
      <div className="eyebrow mb-3">5 roles. Hard-walled scopes.</div>
      <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight max-w-3xl leading-tight">
        Who sees what is decided{" "}
        <span className="italic font-light text-[var(--vs-secondary)]">at the API gate</span> — not in the UI.
      </h2>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-3">
        {roles.map((r, i) => (
          <motion.div
            key={r.t}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="p-5 rounded-md border border-[var(--vs-border)] bg-white"
          >
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--vs-secondary)]">0{i + 1}</div>
            <div className="font-display text-base font-semibold mt-2">{r.t}</div>
            <p className="text-xs text-[var(--vs-text-secondary)] mt-1.5 leading-relaxed">{r.b}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
      <div className="rounded-md border border-[var(--vs-border)] bg-[var(--vs-bg-subtle)] p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight leading-tight">
            Ready to onboard your trust? Sign in with seeded demo accounts to see all 5 dashboards.
          </h3>
          <p className="mt-3 text-sm text-[var(--vs-text-secondary)] max-w-2xl">
            Test credentials are listed below. Every interaction is encrypted and audited.
          </p>
        </div>
        <div className="lg:col-span-4 lg:justify-self-end flex flex-col gap-2">
          <Link
            to="/login?role=trust"
            data-testid="cta-trust-login"
            className="px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] text-center hover:bg-[var(--vs-secondary-hover)] transition-all inline-flex items-center justify-center gap-2"
          >
            <Buildings size={16} weight="fill" /> Trust Admin Login
          </Link>
          <Link
            to="/login"
            data-testid="cta-signin"
            className="px-6 py-3 rounded-md border border-[var(--vs-secondary)] text-[var(--vs-secondary)] text-center hover:bg-[var(--vs-secondary)] hover:text-[var(--vs-bg)] transition-all"
          >
            All other roles
          </Link>
          <a
            href="#security"
            className="px-6 py-3 rounded-md border border-[var(--vs-text)] text-[var(--vs-text)] text-center hover:bg-[var(--vs-text)] hover:text-[var(--vs-bg)] transition-all"
          >
            Re-read security model
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--vs-border)] bg-[var(--vs-bg-subtle)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">Vidya Seva Welfare Trust</div>
            <div className="text-xs text-[var(--vs-text-secondary)]">Empowering futures through education</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono text-[var(--vs-secondary)]">
          <Link to="/verify" data-testid="footer-verify-link" className="underline underline-offset-4 hover:text-[var(--vs-secondary-hover)]">Verify a certificate</Link>
          <span className="opacity-50">•</span>
          <LockKey size={14} weight="fill" /> AES-256-GCM at rest <span className="opacity-50">•</span>
          <ShieldCheck size={14} weight="fill" /> TLS 1.2+ in transit
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="grain-bg min-h-screen">
      <NavBar />
      <Hero />
      <Features />
      <Security />
      <Scale />
      <Roles />
      <CTA />
      <Footer />
    </div>
  );
}
